import { useCallback, useEffect, useState } from 'react'

import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CreateObjectStepId } from 'entities/kyrgyzstan/model/create-object'
import { CreateObjectFieldCard } from 'entities/kyrgyzstan/ui/create-object'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import type {
  CreateObjectLocationCity,
  CreateObjectLocationRegion,
} from 'shared/api/kyrgyzstan/create-object'
import type { ApiFieldErrorMap } from 'shared/lib/form'
import { applyApiFieldErrors } from 'shared/lib/form'
import { Button } from 'shared/ui/button'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'
import { InputWithLabel } from 'shared/ui/input-label'
import { Option, Select } from 'shared/ui/select'

import {
  mapCreateObjectAddressDraftToRequest,
  mapCreateObjectAddressResponseToDraft,
} from '../../lib/address'
import type { CreateObjectAddressDraft } from '../../lib/address'
import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { AddressStepSkeleton } from './ui/address-step-skeleton'

import {
  createObjectAddressFormDefaultValues,
  createObjectAddressFormResolver,
  mapCreateObjectAddressResponseToFormValues,
} from './lib/address-step-form'
import type { CreateObjectAddressFormValues } from './lib/address-step-form'
import { shouldShowAddressMapButton } from './lib/address-step-navigation'
import { mapAddressFormValuesToDraft } from './lib/map-address-form-values-to-draft'
import s from './styles.module.scss'

type AddressStepOperation = 'idle' | 'loading' | 'loading-cities' | 'submitting'

const mapRegionToOption = (region: CreateObjectLocationRegion): Option => ({
  value: region.id,
  name: region.name,
})

const mapCityToOption = (city: CreateObjectLocationCity): Option => ({
  value: String(city.id),
  name: city.name,
})

const addressStepApiFieldMap: ApiFieldErrorMap<CreateObjectAddressFormValues> =
  {
    region_id: 'regionId',
    city_id: 'cityId',
    street: 'street',
    house_number: 'houseNumber',
    geo: 'street',
  }

export const AddressStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
  onOpenStep,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [regions, setRegions] = useState<CreateObjectLocationRegion[]>([])
  const [cities, setCities] = useState<CreateObjectLocationCity[]>([])
  const [addressDraft, setAddressDraft] =
    useState<CreateObjectAddressDraft | null>(null)
  const [operation, setOperation] = useState<AddressStepOperation>('idle')
  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
  } = useForm<CreateObjectAddressFormValues>({
    defaultValues: createObjectAddressFormDefaultValues,
    mode: 'onChange',
    resolver: createObjectAddressFormResolver,
  })
  const regionId = useWatch({ control, name: 'regionId' }) ?? ''

  useEffect(() => {
    const abortController = new AbortController()

    const fetchAddress = async () => {
      setOperation('loading')

      try {
        const [{ data: addressData }, { data: regionsData }] =
          await Promise.all([
            KyrgyzstanCreateObjectApi.getAddressStep(
              draftId,
              abortController.signal,
            ),
            KyrgyzstanCreateObjectApi.getLocationRegions(
              abortController.signal,
            ),
          ])
        const address = addressData.data

        setRegions(regionsData.data)
        setAddressDraft(mapCreateObjectAddressResponseToDraft(address))

        const nextFormValues =
          mapCreateObjectAddressResponseToFormValues(address)

        reset(nextFormValues)

        if (nextFormValues.regionId) {
          const { data: citiesData } =
            await KyrgyzstanCreateObjectApi.getLocationCities(
              nextFormValues.regionId,
              abortController.signal,
            )

          setCities(citiesData.data)
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchAddress()

    return () => {
      abortController.abort()
    }
  }, [draftId, reset, showApiErrorDialog])

  const handleRegionChange = async (
    option: Option,
    onChange: (value: string) => void,
  ) => {
    const nextRegionId = option.value

    onChange(nextRegionId)
    setValue('cityId', '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setCities([])

    setOperation('loading-cities')

    try {
      const { data } =
        await KyrgyzstanCreateObjectApi.getLocationCities(nextRegionId)

      setCities(data.data)
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation('idle')
    }
  }

  const handleSubmitAddress = useCallback(
    async (
      formValues: CreateObjectAddressFormValues,
      targetStepId?: CreateObjectStepId,
    ) => {
      const nextAddressDraft = mapAddressFormValuesToDraft(
        formValues,
        addressDraft,
      )

      setOperation('submitting')

      try {
        await KyrgyzstanCreateObjectApi.saveAddressStep(
          draftId,
          mapCreateObjectAddressDraftToRequest(nextAddressDraft),
        )

        setAddressDraft(nextAddressDraft)

        if (targetStepId) {
          onOpenStep(targetStepId)

          return
        }

        onNext()
      } catch (error) {
        if (!applyApiFieldErrors(error, setError, addressStepApiFieldMap)) {
          showApiErrorDialog(error)
        }
      } finally {
        setOperation('idle')
      }
    },
    [addressDraft, draftId, onNext, onOpenStep, setError, showApiErrorDialog],
  )

  const handleContinue = useCallback(() => {
    void handleSubmit((formValues) => handleSubmitAddress(formValues))()
  }, [handleSubmit, handleSubmitAddress])

  const handleSelectOnMap = useCallback(() => {
    void handleSubmit((formValues) =>
      handleSubmitAddress(formValues, CreateObjectStepId.AddressMap),
    )()
  }, [handleSubmit, handleSubmitAddress])

  const regionOptions = regions.map(mapRegionToOption)
  const cityOptions = cities.map(mapCityToOption)
  const isEditModePristine = mode === 'edit' && !isDirty
  const isContinueDisabled =
    !isValid || operation !== 'idle' || isEditModePristine
  const showMapButton = shouldShowAddressMapButton(mode)

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isContinueDisabled}
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.address.title')}
    >
      <div className={clsx(s.container, 'container')}>
        {operation === 'loading' ? (
          <AddressStepSkeleton />
        ) : (
          <>
            {showMapButton && (
              <Button
                type="button"
                variant="white"
                borderRadius="md"
                className={clsx(s.mapButton, 'semi-bold-17')}
                disabled={operation !== 'idle'}
                onClick={handleSelectOnMap}
              >
                {t(
                  'kyrgyzstan_block.create_object.steps.address.select_on_map',
                )}
              </Button>
            )}

            <CreateObjectFieldCard
              title={t('kyrgyzstan_block.create_object.steps.address.address')}
            >
              <Controller
                control={control}
                name="regionId"
                render={({ field }) => (
                  <Select
                    variant="secondary"
                    title={t(
                      'kyrgyzstan_block.create_object.steps.address.region',
                    )}
                    placeholder={t(
                      'kyrgyzstan_block.create_object.steps.address.region',
                    )}
                    value={field.value}
                    options={regionOptions}
                    error={errors.regionId}
                    disabled={operation !== 'idle'}
                    onChange={(option) =>
                      handleRegionChange(option, field.onChange)
                    }
                  />
                )}
              />

              <Controller
                control={control}
                name="cityId"
                render={({ field }) => (
                  <div className={s.fieldWithLoader}>
                    <Select
                      variant="secondary"
                      title={t(
                        'kyrgyzstan_block.create_object.steps.address.city',
                      )}
                      placeholder={t(
                        'kyrgyzstan_block.create_object.steps.address.city',
                      )}
                      value={field.value}
                      options={cityOptions}
                      error={
                        !field.value && errors.cityId?.type !== 'server'
                          ? false
                          : errors.cityId
                      }
                      disabled={!regionId || operation !== 'idle'}
                      onChange={(option) => field.onChange(option.value)}
                    />

                    {operation === 'loading-cities' && (
                      <span className={s.fieldLoader}>
                        <span className={s.spinner} />
                      </span>
                    )}
                  </div>
                )}
              />
            </CreateObjectFieldCard>

            <CreateObjectFieldCard
              title={t('kyrgyzstan_block.create_object.steps.address.object')}
            >
              <InputWithLabel
                {...register('street')}
                variant="secondary"
                label={t('kyrgyzstan_block.create_object.steps.address.street')}
                error={errors.street}
                disabled={operation !== 'idle'}
              />

              <InputWithLabel
                {...register('houseNumber')}
                variant="secondary"
                label={t('kyrgyzstan_block.create_object.steps.address.house')}
                error={errors.houseNumber}
                disabled={operation !== 'idle'}
              />
            </CreateObjectFieldCard>
          </>
        )}
      </div>
    </CreateObjectStepLayout>
  )
}
